import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Calendar, MapPin } from "lucide-react";
import { toast } from "sonner";
import { epsteinTimelineEvent } from "../lib/api";
import { KIND_META, CONFIDENCE_META } from "../components/epstein/efConsts";

const EpsteinTimeline = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const r = await epsteinTimelineEvent(slug);
        if (active) setData(r);
      } catch {
        toast.error("Timeline event not found.");
        if (active) setData(null);
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center font-mono text-[#757065]">// LOADING EVENT…</div>;
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex flex-col items-center justify-center gap-3" data-testid="ef-timeline-404">
        <div className="font-mono text-xs text-[#757065]">// EVENT NOT FOUND</div>
        <Link to="/epstein-files" className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">← back to the file</Link>
      </div>
    );
  }

  const { event, entities, evidence_items, sources } = data;
  const conf = CONFIDENCE_META[event.confidence_level] || null;

  return (
    <div className="min-h-screen bg-[#0F0F0F]" data-testid={`ef-timeline-page-${event.id}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-10 py-6 md:py-10">
        <Link to="/epstein-files" data-testid="ef-timeline-back"
          className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] hover:text-[#D92D20] mb-6">
          <ArrowLeft size={12} /> Back to evidence board
        </Link>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] border border-[#D92D20] px-2 py-0.5">
            <Calendar size={10} className="inline mr-1" /> {event.date || "DATE UNKNOWN"}
          </span>
          {event.event_type && (
            <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#9FA8D4] border border-[#9FA8D4]/40 px-2 py-0.5">
              {event.event_type.replace(/_/g, " ")}
            </span>
          )}
          {conf && (
            <span style={{ color: conf.color, borderColor: conf.color }}
              className="font-mono text-[9px] uppercase tracking-[0.3em] border px-2 py-0.5">
              {conf.label}
            </span>
          )}
        </div>

        <h1 className="font-display text-3xl sm:text-4xl text-[#E4DFD3] leading-tight mb-3">{event.title}</h1>

        {event.location_label && (
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#A39E93] mb-3 flex items-center gap-1">
            <MapPin size={11} /> {event.location_label}
          </div>
        )}

        {event.summary && (
          <p className="text-base text-[#A39E93] leading-relaxed mb-4">{event.summary}</p>
        )}

        {event.legal_note && (
          <div className="border border-[#E0A526]/40 bg-[#1a1408] px-3 py-2 mb-5 text-[12px] italic text-[#CBB892]">
            ⚠ {event.legal_note}
          </div>
        )}

        <section className="mb-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-3">// INVOLVED ENTITIES · {entities.length}</div>
          {entities.length === 0 ? (
            <div className="font-mono text-xs text-[#757065]">None.</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-2">
              {entities.map((e) => {
                const m = KIND_META[e.kind] || KIND_META.reference;
                return (
                  <Link key={e.id} to={`/epstein-files/entity/${e.slug}`}
                    className="border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2 hover:border-[#D92D20]/60 block"
                    data-testid={`ef-timeline-entity-${e.id}`}>
                    <span style={{ color: m.color }} className="font-mono text-[8px] uppercase tracking-[0.25em]">{m.label}</span>
                    <div className="font-heading text-sm text-[#E4DFD3]">{e.name}</div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        {evidence_items.length > 0 && (
          <section className="mb-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-3">// EVIDENCE · {evidence_items.length}</div>
            <div className="space-y-2">
              {evidence_items.map((ev) => (
                <Link key={ev.id} to={`/epstein-files/evidence/${ev.slug}`}
                  className="border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2 hover:border-[#D92D20]/60 block"
                  data-testid={`ef-timeline-evidence-${ev.id}`}>
                  <span className="font-mono text-[8px] uppercase tracking-[0.3em] text-[#5DD4B3]">{(ev.evidence_type || "evidence").replace(/_/g, " ")}</span>
                  <div className="font-heading text-sm text-[#E4DFD3]">{ev.title}</div>
                  {ev.date && <div className="font-mono text-[10px] text-[#757065]">{ev.date}</div>}
                </Link>
              ))}
            </div>
          </section>
        )}

        <section>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-3">// SOURCES · {sources.length}</div>
          {sources.length === 0 ? (
            <div className="font-mono text-xs text-[#757065]">None on file.</div>
          ) : (
            <div className="space-y-2">
              {sources.map((s) => (
                <Link key={s.id} to={`/epstein-files/source/${s.id}`}
                  className="border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2 hover:border-[#D92D20]/60 block"
                  data-testid={`ef-timeline-source-${s.id}`}>
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="font-mono text-[8px] uppercase tracking-[0.3em] text-[#5DD4B3]">{(s.source_type || "source").replace(/_/g, " ")}</span>
                    {s.publisher && <span className="font-mono text-[8px] text-[#757065]">{s.publisher}</span>}
                  </div>
                  <div className="font-heading text-sm text-[#E4DFD3]">{s.title}</div>
                  <div className="font-mono text-[10px] text-[#A39E93] break-all">
                    {s.url} <ExternalLink size={9} className="inline" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        <div className="border border-[#2D2A26] bg-[#0a0907] px-3 py-2 mt-8 text-[11px] italic text-[#757065]">
          Inclusion does not imply guilt or criminal conduct. Read each linked source.
        </div>
      </div>
    </div>
  );
};

export default EpsteinTimeline;
